import React, { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { api, fmtFecha, initials } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/roles";
import { Users, Cake, Palmtree, UserCheck, AlertTriangle, ListChecks, Circle, Sparkles, ArrowRight, ArrowUpCircle, FileSignature, Upload, CalendarDays, Bell, Heart, Briefcase, Clock, ChevronLeft, ChevronRight, X, Plus, CreditCard, ClipboardList, Users as UsersIcon } from "lucide-react";
import { calcularSaldoVacaciones } from "@/lib/vacaciones";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const KPI = ({ icon: Icon, label, value, sub, color = "primary" }) => (
  <div className="np-card fade-in-up">
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color === "accent" ? "bg-[#FDF1EC] text-[#D96C4A]" : color === "warning" ? "bg-[#FFF4E5] text-[#A67115]" : color === "error" ? "bg-[#FDEDED] text-[#B84B4B]" : "bg-[#E6EFEA] text-[#274735]"}`}>
        <Icon size={20} strokeWidth={1.8} />
      </div>
    </div>
    <div className="font-display text-3xl font-semibold text-primary-c leading-none">{value}</div>
    <div className="text-sm font-medium mt-2">{label}</div>
    {sub && <div className="text-xs text-secondary-c mt-1">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const [empleados, setEmpleados] = useState([]);
  const [asistenciaAll, setAsistenciaAll] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [accionesHR, setAccionesHR] = useState([]);
  const [accionesDocs, setAccionesDocs] = useState([]);
  const [customEvents, setCustomEvents] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [incapacidades, setIncapacidades] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Rango amplio de incapacidades: 6 meses atrás → 6 meses adelante
        const hoy = new Date();
        const iniISO = new Date(hoy.getFullYear(), hoy.getMonth() - 6, 1).toISOString().slice(0, 10);
        const finISO = new Date(hoy.getFullYear(), hoy.getMonth() + 6, 0).toISOString().slice(0, 10);
        const [e, all, s, c, aHR, aDocs, cEv, emp, inc] = await Promise.all([
          api.get("/empleados"),
          api.get("/asistencia"),
          api.get("/solicitudes?estado=pendiente"),
          api.get("/contratos"),
          api.get("/acciones-personal"),
          api.get("/acciones-documentos").catch(() => ({ data: [] })),
          api.get("/calendar-events").catch(() => ({ data: [] })),
          api.get("/empresa").catch(() => ({ data: null })),
          api.get(`/incapacidades?fecha_inicio=${iniISO}&fecha_fin=${finISO}`),
        ]);
        setEmpleados(e.data);
        setAsistenciaAll(all.data || []);
        setSolicitudes(s.data || []);
        setContratos(c.data || []);
        setAccionesHR(aHR.data || []);
        setAccionesDocs(aDocs.data || []);
        setCustomEvents(cEv.data || []);
        setEmpresa(emp.data);
        setIncapacidades(inc.data || []);
        // Audit log — solo admin puede leerlo; ignoramos error 403 para asistentes
        if (isAdmin) {
          try {
            const audit = await api.get("/audit-log?limit=10");
            setAuditLog(audit.data || []);
          } catch (err) { console.error("Dashboard: audit-log fetch failed", err); }
        }
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const marcarRealizada = async (sol) => {
    try {
      await api.put(`/solicitudes/${sol.id}`, { estado: "realizada" });
      setSolicitudes(prev => prev.filter(s => s.id !== sol.id));
      toast.success("Solicitud marcada como realizada");
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const empMap = useMemo(() => {
    const m = {};
    empleados.forEach(e => { m[e.id] = `${e.nombre} ${e.apellidos || ""}`.trim(); });
    return m;
  }, [empleados]);

  // Empleados activos sin contrato adjunto
  const sinContrato = useMemo(() => {
    const conSet = new Set(contratos.map(c => c.empleado_id));
    return empleados.filter(e => e.estado === "Activo" && !conSet.has(e.id));
  }, [empleados, contratos]);

  const activos = empleados.filter(e => e.estado === "Activo");

  // Cumpleaños próximos: empleados activos con fecha_nacimiento, próximos 30 días
  const cumpleanios = useMemo(() => {
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const out = [];
    for (const emp of activos) {
      if (!emp.fecha_nacimiento) continue;
      const raw = String(emp.fecha_nacimiento).slice(0, 10);
      const fn = new Date(`${raw}T00:00:00`);
      if (isNaN(fn.getTime())) continue; // fecha malformada → ignorar
      const proximo = new Date(hoy.getFullYear(), fn.getMonth(), fn.getDate());
      if (isNaN(proximo.getTime())) continue;
      if (proximo < hoy) proximo.setFullYear(proximo.getFullYear() + 1);
      const dias = Math.round((proximo - hoy) / 86400000);
      if (dias > 30) continue;
      const edad = proximo.getFullYear() - fn.getFullYear();
      out.push({ emp, fecha: proximo.toISOString().slice(0, 10), dias, edad });
    }
    return out.sort((a, b) => a.dias - b.dias);
  }, [activos]);
  const proximoCumple = cumpleanios[0];

  // Top vacaciones acumuladas
  const topVacaciones = useMemo(() => {
    return activos
      .map(emp => ({ emp, ...calcularSaldoVacaciones(emp, asistenciaAll, new Date(), empresa) }))
      .sort((a, b) => b.saldo - a.saldo)
      .slice(0, 5);
  }, [activos, asistenciaAll, empresa]);
  const maxSaldo = Math.max(1, ...topVacaciones.map(r => r.saldo));

  // Revisión de trabajadores: próximos a cumplir 3, 6 o 12 meses (ventana: -7 a +60 días)
  const revisiones = useMemo(() => {
    const MILESTONES = [3, 6, 12];
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const out = [];
    for (const emp of activos) {
      if (!emp.fecha_ingreso) continue;
      const ing = new Date(String(emp.fecha_ingreso).slice(0, 10) + "T00:00:00");
      if (isNaN(ing.getTime())) continue;
      for (const m of MILESTONES) {
        const target = new Date(ing);
        target.setMonth(target.getMonth() + m);
        if (isNaN(target.getTime())) continue;
        const dias = Math.ceil((target - t) / (1000 * 60 * 60 * 24));
        if (dias < -7 || dias > 60) continue;
        out.push({ emp, meses: m, fecha: target.toISOString().slice(0, 10), diasRestantes: dias });
      }
    }
    return out.sort((a, b) => a.diasRestantes - b.diasRestantes);
  }, [activos]);
  const revisionesUrgentes = revisiones.filter(r => r.diasRestantes >= 0 && r.diasRestantes <= 15);

  // === Eventos unificados (para el calendario y los recordatorios) ===
  // Cada evento: { id, fecha:"YYYY-MM-DD", tipo, titulo, empleadoNombre?, sub?, color, url }
  const eventos = useMemo(() => {
    const out = [];
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const hoyISO = toISOLocal(hoy);
    const fin = new Date(hoy); fin.setMonth(fin.getMonth() + 12);
    const finISO = toISOLocal(fin);

    // Cumpleaños (próximos 12 meses, celebración en el año correspondiente)
    for (const emp of activos) {
      if (!emp.fecha_nacimiento) continue;
      const raw = String(emp.fecha_nacimiento).slice(0, 10);
      const fn = new Date(`${raw}T00:00:00`);
      if (isNaN(fn.getTime())) continue;
      for (let y = hoy.getFullYear(); y <= hoy.getFullYear() + 1; y++) {
        const iso = `${y}-${String(fn.getMonth() + 1).padStart(2, "0")}-${String(fn.getDate()).padStart(2, "0")}`;
        if (iso < hoyISO || iso > finISO) continue;
        const edad = y - fn.getFullYear();
        out.push({ id: `cumple-${emp.id}-${y}`, fecha: iso, tipo: "cumpleanios", titulo: `Cumpleaños de ${emp.nombre}`, empleadoNombre: `${emp.nombre} ${emp.apellidos || ""}`.trim(), sub: `${edad} años`, color: "#D96C4A", url: "/empleados" });
      }
    }

    // Incapacidades: un evento por rango; también marcamos los días intermedios en el calendario
    for (const inc of incapacidades) {
      const empNombre = empMap[inc.empleado_id] || "Empleado";
      if (!inc.fecha_inicio) continue;
      const dias = daysDiff(inc.fecha_inicio, inc.fecha_fin || inc.fecha_inicio) + 1;
      out.push({
        id: `inc-${inc.id}`,
        fecha: inc.fecha_inicio,
        fechaFin: inc.fecha_fin || inc.fecha_inicio,
        tipo: "incapacidad",
        titulo: `Incapacidad · ${empNombre}`,
        empleadoNombre: empNombre,
        sub: `${inc.tipo || "CCSS"} · ${dias} ${dias === 1 ? "día" : "días"}`,
        color: "#B84B4B",
        url: "/incapacidades",
      });
    }

    // Vacaciones registradas (asistencia estado "Vacaciones") — agrupadas por rango contiguo
    const vacGroups = groupContiguous(
      asistenciaAll.filter(a => (a.estado || "") === "Vacaciones")
    );
    for (const g of vacGroups) {
      const empNombre = empMap[g.empleado_id] || "Empleado";
      out.push({
        id: `vac-${g.empleado_id}-${g.fecha_inicio}`,
        fecha: g.fecha_inicio,
        fechaFin: g.fecha_fin,
        tipo: "vacaciones",
        titulo: `Vacaciones · ${empNombre}`,
        empleadoNombre: empNombre,
        sub: `${g.dias} ${g.dias === 1 ? "día" : "días"}`,
        color: "#2F7A5F",
        url: "/vacaciones",
      });
    }

    // Solicitudes pendientes con fecha
    for (const sol of solicitudes) {
      const f = (sol.fecha_solicitud || "").slice(0, 10);
      if (!f) continue;
      const empNombre = sol.empleado_id ? empMap[sol.empleado_id] : "";
      out.push({
        id: `sol-${sol.id}`,
        fecha: f,
        tipo: "solicitud",
        titulo: sol.tipo || "Solicitud",
        empleadoNombre: empNombre,
        sub: sol.descripcion ? sol.descripcion.slice(0, 60) : "",
        color: "#A67115",
        url: "/solicitudes",
      });
    }

    // Fin del período de prueba (ascensos activos)
    for (const a of accionesHR) {
      if (a.tipo !== "ascenso" || a.estado !== "activo" || !a.fin_periodo_prueba) continue;
      const empNombre = empMap[a.empleado_id] || "Empleado";
      out.push({
        id: `asc-${a.id}`,
        fecha: String(a.fin_periodo_prueba).slice(0, 10),
        tipo: "ascenso",
        titulo: `Fin período prueba · ${empNombre}`,
        empleadoNombre: empNombre,
        sub: a.puesto_nuevo ? `→ ${a.puesto_nuevo}` : "",
        color: "#6D4AA6",
        url: "/acciones-personal",
      });
    }

    // Revisiones 3/6/12 meses
    for (const r of revisiones) {
      out.push({
        id: `rev-${r.emp.id}-${r.meses}`,
        fecha: r.fecha,
        tipo: "revision",
        titulo: `Revisión ${r.meses} ${r.meses === 1 ? "mes" : "meses"}`,
        empleadoNombre: `${r.emp.nombre} ${r.emp.apellidos || ""}`.trim(),
        sub: `Ingreso: ${fmtFecha(r.emp.fecha_ingreso)}`,
        color: "#274735",
        url: "/empleados",
      });
    }

    // Acciones de personal generadas (permisos, amonestaciones, etc.) — todas se muestran en el calendario
    for (const doc of accionesDocs) {
      if (!doc.fecha_emision && !doc.created_at) continue;
      const fecha = String(doc.fecha_emision || doc.created_at).slice(0, 10);
      const empNombre = doc.empleado_id ? empMap[doc.empleado_id] : "";
      out.push({
        id: `accdoc-${doc.id}`,
        fecha,
        tipo: "accion_documento",
        titulo: doc.nombre || doc.categoria || "Acción de personal",
        empleadoNombre: empNombre,
        sub: doc.categoria || "",
        color: "#5A4A7A",
        url: "/acciones-personal",
      });
    }

    // Eventos personalizados creados por el usuario (tareas, reuniones, pagos, etc.)
    for (const ev of customEvents) {
      out.push({
        id: `custom-${ev.id}`,
        fecha: ev.fecha,
        tipo: `custom_${ev.tipo || "otro"}`,
        titulo: ev.titulo,
        empleadoNombre: "",
        sub: ev.hora ? `${ev.hora}${ev.descripcion ? " · " + ev.descripcion : ""}` : (ev.descripcion || ""),
        color: ev.color || "#274735",
        url: "/dashboard",
        isCustom: true,
        customId: ev.id,
      });
    }

    // Fecha de próximo pago de la suscripción del sistema
    if (empresa?.proximo_pago_at && !empresa?.es_gratuita) {
      const fecha = String(empresa.proximo_pago_at).slice(0, 10);
      out.push({
        id: `pago-suscripcion-${fecha}`,
        fecha,
        tipo: "pago_suscripcion",
        titulo: "Pago de suscripción del sistema",
        empleadoNombre: "",
        sub: "Vence tu ciclo de facturación",
        color: "#B84B4B",
        url: "/pagos",
      });
    }

    return out.sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [activos, incapacidades, asistenciaAll, solicitudes, accionesHR, accionesDocs, customEvents, empresa, revisiones, empMap]);

  // Ascensos en período de prueba: próximos 15 días antes de cumplir 3 meses (o ya vencidos)
  const revisionesAscenso = useMemo(() => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    return accionesHR
      .filter(a => a.tipo === "ascenso" && a.estado === "activo" && a.fin_periodo_prueba)
      .map(a => {
        const target = new Date(String(a.fin_periodo_prueba).slice(0, 10) + "T00:00:00");
        if (isNaN(target.getTime())) return null;
        const dias = Math.ceil((target - t) / (1000 * 60 * 60 * 24));
        const emp = empleados.find(e => e.id === a.empleado_id);
        return { accion: a, emp, diasRestantes: dias };
      })
      .filter(r => r && r.emp && r.diasRestantes <= 15) // urgente ó vencido
      .sort((a, b) => a.diasRestantes - b.diasRestantes);
  }, [accionesHR, empleados]);

  return (
    <Layout>
      <header className="mb-8 fade-in-up">
        <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold mb-2">Resumen general</div>
        <h1 className="font-display text-4xl font-semibold">Dashboard</h1>
        <p className="text-sm text-secondary-c mt-1">Vista panorámica de personal, asistencia y nómina.</p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8 items-stretch">
        <KPI icon={Users} label="Empleados Activos" value={activos.length} sub={`${empleados.length} registrados en total`} />
        <div className="np-card lg:col-span-2 fade-in-up" data-testid="dashboard-cumpleanios">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-[#FDF1EC] text-[#D96C4A] flex items-center justify-center">
                <Cake size={20} strokeWidth={1.8} />
              </div>
              <div>
                <h3 className="font-display text-xl font-semibold leading-tight">Cumpleaños próximos</h3>
                <p className="text-xs text-secondary-c">
                  {cumpleanios.length === 0
                    ? "Sin cumpleaños en los próximos 30 días"
                    : proximoCumple.dias === 0
                    ? `🎉 ¡Hoy es el cumpleaños de ${proximoCumple.emp.nombre}!`
                    : proximoCumple.dias === 1
                    ? `Mañana cumple ${proximoCumple.emp.nombre}`
                    : `${cumpleanios.length} en los próximos 30 días · El próximo: ${proximoCumple.emp.nombre} en ${proximoCumple.dias} días`}
                </p>
              </div>
            </div>
            <span className="font-display text-3xl font-semibold text-primary-c" data-testid="cumple-count">{cumpleanios.length}</span>
          </div>
          {cumpleanios.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-8 rounded-xl bg-[#FAFAF7] border border-dashed border-c">
              <Cake size={28} className="text-secondary-c opacity-60" />
              <div className="text-sm text-secondary-c">Ningún empleado cumple años en los próximos 30 días.</div>
              <div className="text-xs text-secondary-c opacity-70">Agregá la fecha de nacimiento en el perfil de cada empleado.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {cumpleanios.map(({ emp, fecha, dias, edad }) => {
                const isHoy = dias === 0;
                const isManiana = dias === 1;
                const isProximo = dias <= 7;
                return (
                  <div
                    key={emp.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border ${isHoy ? "border-[#F0CC8C] bg-[#FDF1EC]" : isProximo ? "border-[#F0CC8C] bg-[#FFF7E5]" : "border-c bg-white"}`}
                    data-testid={`cumple-row-${emp.cedula}`}
                  >
                    {emp.avatar ? (
                      <img src={emp.avatar} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#FDF1EC] text-[#D96C4A] flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {initials(`${emp.nombre} ${emp.apellidos}`)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{emp.nombre} {emp.apellidos}</div>
                      <div className="text-xs text-secondary-c truncate">{emp.puesto || "—"} · cumple {edad} años</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`text-xs font-semibold ${isHoy ? "text-[#D96C4A]" : isProximo ? "text-warning" : "text-secondary-c"}`}>
                        {isHoy ? "🎉 ¡HOY!" : isManiana ? "Mañana" : `${dias} días`}
                      </div>
                      <div className="text-[11px] text-secondary-c mt-0.5">{fmtFecha(fecha)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Calendario del mes + Recordatorios/Solicitudes en 2 columnas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div className="lg:col-span-3">
          <MonthCalendar
            eventos={eventos}
            onCreateEvent={async (data) => {
              const { data: created } = await api.post("/calendar-events", data);
              setCustomEvents(prev => [...prev, created]);
              toast.success("Evento agregado");
            }}
            onDeleteCustomEvent={async (id) => {
              try {
                await api.delete(`/calendar-events/${id}`);
                setCustomEvents(prev => prev.filter(e => e.id !== id));
                toast.success("Evento eliminado");
              } catch (err) { toast.error("No se pudo eliminar"); }
            }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {/* Recordatorios (próximos eventos) */}
        <Recordatorios eventos={eventos} />

        {/* To-Do de solicitudes pendientes */}
        <div className="np-card" data-testid="dashboard-solicitudes">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-[#FFF4E5] text-warning flex items-center justify-center">
                <ListChecks size={20} strokeWidth={1.8} />
              </div>
              <div>
                <h3 className="font-display text-xl font-semibold leading-tight">Solicitudes pendientes</h3>
                <p className="text-xs text-secondary-c">
                  {solicitudes.length === 0
                    ? "No hay solicitudes pendientes ✨"
                    : `${solicitudes.length} ${solicitudes.length === 1 ? "tarea por hacer" : "tareas por hacer"}`}
                </p>
              </div>
            </div>
            <Link to="/solicitudes" className="text-xs text-primary-c font-medium hover:underline flex items-center gap-1" data-testid="dashboard-solicitudes-link">
              Ver todas <ArrowRight size={12} />
            </Link>
          </div>
          {solicitudes.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-8 rounded-xl bg-[#FAFAF7] border border-dashed border-c">
              <Sparkles size={28} className="text-secondary-c opacity-60" />
              <div className="text-sm text-secondary-c">Todo al día. Las solicitudes nuevas aparecerán acá.</div>
              <Link to="/solicitudes" className="text-xs text-primary-c font-medium underline mt-1">Registrar una solicitud</Link>
            </div>
          ) : (
            <div className="space-y-2" data-testid="dashboard-solicitudes-list">
              {solicitudes.slice(0, 6).map(sol => {
                const empNombre = sol.empleado_id ? empMap[sol.empleado_id] : null;
                return (
                  <div key={sol.id} className="flex items-start gap-3 p-3 rounded-xl border border-c bg-white hover:bg-[#FAFAF7] transition-colors" data-testid={`dash-sol-${sol.id}`}>
                    <button
                      onClick={() => marcarRealizada(sol)}
                      className="mt-0.5 flex-shrink-0 transition-transform hover:scale-110"
                      title="Marcar como realizada"
                      data-testid={`dash-sol-check-${sol.id}`}
                    >
                      <Circle size={20} className="text-secondary-c hover:text-success" />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm">{sol.tipo}</div>
                          {empNombre && (
                            <div className="flex items-center gap-1.5 mt-0.5 text-xs text-secondary-c">
                              <span className="w-4 h-4 rounded-full bg-[#E6EFEA] text-primary-color flex items-center justify-center text-[9px] font-semibold">
                                {initials(empNombre)}
                              </span>
                              {empNombre}
                            </div>
                          )}
                          {sol.descripcion && <div className="text-xs text-secondary-c mt-1 line-clamp-1">{sol.descripcion}</div>}
                        </div>
                        <div className="text-right text-[11px] text-secondary-c flex-shrink-0">
                          {fmtFecha(sol.fecha_solicitud)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {solicitudes.length > 6 && (
                <Link to="/solicitudes" className="block text-center text-xs text-primary-c font-medium hover:underline pt-2">
                  Ver las {solicitudes.length - 6} solicitudes restantes →
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Empleados sin contrato */}
      {isAdmin && (
      <div className="np-card mb-6" data-testid="dashboard-sin-contrato">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-[#FDEDED] text-error flex items-center justify-center">
              <FileSignature size={20} strokeWidth={1.8} />
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold leading-tight">Empleados sin contrato</h3>
              <p className="text-xs text-secondary-c">
                {sinContrato.length === 0
                  ? "Todos los activos tienen contrato adjunto ✨"
                  : `${sinContrato.length} ${sinContrato.length === 1 ? "trabajador" : "trabajadores"} con contrato pendiente`}
              </p>
            </div>
          </div>
          <Link to="/contratos" className="text-xs text-primary-c font-medium hover:underline flex items-center gap-1" data-testid="dashboard-contratos-link">
            Ir a contratos <ArrowRight size={12} />
          </Link>
        </div>
        {sinContrato.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8 rounded-xl bg-[#FAFAF7] border border-dashed border-c">
            <Sparkles size={28} className="text-secondary-c opacity-60" />
            <div className="text-sm text-secondary-c">Sin pendientes. Todos los empleados activos tienen contrato.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-testid="dashboard-sin-contrato-list">
            {sinContrato.slice(0, 6).map(emp => (
              <Link
                key={emp.id} to="/contratos"
                className="flex items-center gap-3 p-3 rounded-xl border border-c bg-white hover:border-primary-c hover:bg-[#FAFAF7] transition-colors"
                data-testid={`dash-sincontrato-${emp.cedula}`}
              >
                {emp.avatar ? (
                  <img src={emp.avatar} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#FDEDED] text-error flex items-center justify-center text-xs font-semibold flex-shrink-0">
                    {initials(`${emp.nombre} ${emp.apellidos}`)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{emp.nombre} {emp.apellidos}</div>
                  <div className="text-xs text-secondary-c truncate">{emp.puesto || "—"}</div>
                </div>
                <Upload size={14} className="text-secondary-c flex-shrink-0" />
              </Link>
            ))}
            {sinContrato.length > 6 && (
              <Link to="/contratos" className="sm:col-span-2 lg:col-span-3 block text-center text-xs text-primary-c font-medium hover:underline pt-2">
                Ver los {sinContrato.length - 6} restantes →
              </Link>
            )}
          </div>
        )}
      </div>
      )}

      {revisionesUrgentes.length > 0 && isAdmin && (
        <div className="np-card mb-6 p-4 border-l-4 border-warning bg-[#FFF7E5]" data-testid="dashboard-revision-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-warning mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-display text-base font-semibold mb-1">Revisiones de personal próximas</div>
              <div className="text-sm text-secondary-c space-y-1">
                {revisionesUrgentes.map((r) => (
                  <div key={`rev-${r.emp.id}-${r.meses}`}>
                    <strong className="text-primary-c">{r.emp.nombre} {r.emp.apellidos}</strong> está próximo a cumplir <strong>{r.meses} {r.meses === 1 ? "mes" : "meses"}</strong> el {fmtFecha(r.fecha)}
                    {" — "}
                    <span className={r.diasRestantes <= 7 ? "text-error font-medium" : "text-warning font-medium"}>
                      {r.diasRestantes === 0 ? "es hoy" : r.diasRestantes === 1 ? "es mañana" : `faltan ${r.diasRestantes} días`}
                    </span>
                    . Realizá la revisión correspondiente para evaluar el desempeño antes de que aumente la cesantía acumulada.
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {revisionesAscenso.length > 0 && isAdmin && (
        <div className="np-card mb-6 p-4 border-l-4 border-primary-c bg-[#E6EFEA]" data-testid="dashboard-ascenso-banner">
          <div className="flex items-start gap-3">
            <ArrowUpCircle size={20} className="text-primary-color mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-display text-base font-semibold mb-1">Ascensos en período de prueba — Requieren revisión</div>
              <div className="text-sm text-secondary-c space-y-1">
                {revisionesAscenso.map((r) => (
                  <div key={r.accion.id || r.emp.id}>
                    <strong className="text-primary-c">{r.emp.nombre} {r.emp.apellidos}</strong> fue ascendido a <strong>{r.accion.puesto_nuevo}</strong>. Los 3 meses de prueba
                    {" "}
                    {r.diasRestantes < 0 ? (
                      <span className="text-error font-medium">vencieron hace {-r.diasRestantes} d</span>
                    ) : r.diasRestantes === 0 ? (
                      <span className="text-warning font-medium">se cumplen hoy</span>
                    ) : (
                      <span className="text-warning font-medium">vencen en {r.diasRestantes} d ({fmtFecha(r.accion.fin_periodo_prueba)})</span>
                    )}
                    {" — "}<Link to="/acciones-personal" className="text-primary-c underline font-medium">Ir a Acciones de Personal</Link> para confirmar o revertir.
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {isAdmin && (
        <>
        <div className="np-card lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <UserCheck size={18} className="text-primary-color" />
              <h3 className="font-display text-xl font-semibold">Revisión de Trabajadores</h3>
            </div>
            <span className="text-xs text-secondary-c">Hitos: 3 / 6 / 12 meses</span>
          </div>
          {loading ? (
            <div className="text-sm text-secondary-c">Cargando...</div>
          ) : revisiones.length === 0 ? (
            <div className="text-sm text-secondary-c py-8 text-center">Ningún empleado activo se acerca a un hito de revisión (3, 6 o 12 meses) en los próximos 60 días.</div>
          ) : (
            <div className="space-y-3" data-testid="dashboard-revisiones">
              {revisiones.map((r) => {
                const isUrgente = r.diasRestantes >= 0 && r.diasRestantes <= 15;
                const isVencido = r.diasRestantes < 0;
                const colorBg = isVencido ? "bg-[#FAFAF7]" : isUrgente ? "bg-[#FFF7E5]" : "bg-white";
                const colorBd = isUrgente ? "border-[#F0CC8C]" : "border-c";
                return (
                  <div key={`rev2-${r.emp.id}-${r.meses}`} className={`flex items-center gap-3 p-3 rounded-xl border ${colorBd} ${colorBg}`} data-testid={`revision-row-${r.emp.cedula}-${r.meses}`}>
                    {r.emp.avatar ? (
                      <img src={r.emp.avatar} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#E6EFEA] flex items-center justify-center text-[#274735] text-xs font-semibold flex-shrink-0">{initials(`${r.emp.nombre} ${r.emp.apellidos}`)}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{r.emp.nombre} {r.emp.apellidos}</div>
                      <div className="text-xs text-secondary-c">Ingreso: {fmtFecha(r.emp.fecha_ingreso)} · {r.emp.puesto}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="flex items-center gap-2 justify-end">
                        <span className="np-badge np-badge-info text-xs">{r.meses} {r.meses === 1 ? "mes" : "meses"}</span>
                      </div>
                      <div className={`text-xs mt-1 font-medium ${isVencido ? "text-secondary-c" : isUrgente ? "text-warning" : "text-secondary-c"}`}>
                        {isVencido
                          ? `Cumplido hace ${Math.abs(r.diasRestantes)} día${Math.abs(r.diasRestantes) === 1 ? "" : "s"}`
                          : r.diasRestantes === 0
                          ? "Hoy cumple"
                          : `${fmtFecha(r.fecha)} · faltan ${r.diasRestantes} día${r.diasRestantes === 1 ? "" : "s"}`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="np-card">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-xl font-semibold">Top vacaciones acumuladas</h3>
            <Palmtree size={18} className="text-primary-color opacity-70" />
          </div>
          {topVacaciones.length === 0 ? (
            <div className="text-sm text-secondary-c py-8 text-center">Sin empleados activos.</div>
          ) : (
            <div className="space-y-4" data-testid="dashboard-top-vacaciones">
              {topVacaciones.map(({ emp, generados, disfrutados, saldo, periodoActual }) => {
                const pct = Math.max(0, Math.round((saldo / maxSaldo) * 100));
                return (
                  <div key={emp.id} className="flex items-center gap-3">
                    {emp.avatar
                      ? <img src={emp.avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                      : <div className="w-9 h-9 rounded-full bg-[#E6EFEA] text-primary-color flex items-center justify-center text-xs font-semibold flex-shrink-0">{initials(`${emp.nombre} ${emp.apellidos}`)}</div>}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium truncate">{emp.nombre} {emp.apellidos}</span>
                        <span className={`font-semibold ml-2 ${saldo > 0 ? "text-success" : saldo === 0 ? "text-secondary-c" : "text-warning"}`}>{saldo} {saldo === 1 ? "día" : "días"}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-[#EFEEE8] overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: "#D69E36" }} />
                      </div>
                      <div className="text-[11px] text-secondary-c mt-1">
                        {periodoActual ? <>Período <span className="font-medium">{periodoActual.label}</span> · </> : null}
                        Generados {generados} · Disfrutados {disfrutados}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </>
        )}
      </div>

      {/* Actividad reciente (audit log) — admin — al final del dashboard */}
      {isAdmin && auditLog.length > 0 && (
        <div className="np-card mt-6" data-testid="dashboard-audit-log">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-[#F0F0EA] text-primary-c flex items-center justify-center">
                <ListChecks size={20} strokeWidth={1.8} />
              </div>
              <div>
                <h3 className="font-display text-xl font-semibold leading-tight">Actividad reciente</h3>
                <p className="text-xs text-secondary-c">Últimas {auditLog.length} acciones registradas en el sistema</p>
              </div>
            </div>
          </div>
          <div className="divide-y divide-c">
            {auditLog.map(a => <AuditRow key={a.id} entry={a} />)}
          </div>
        </div>
      )}
    </Layout>
  );
}


// ==================== Audit log row ====================
const ACTION_LABEL = {
  create: "Creó",
  update: "Actualizó",
  delete: "Eliminó",
  send_email: "Envió correo",
  login: "Inició sesión",
};
const ENTITY_LABEL = {
  empleado: "empleado",
  perfil: "perfil",
  empresa: "configuración de empresa",
  accion_hr: "acción de personal",
  contrato: "contrato",
  comprobante: "comprobante de pago",
};
const ACTION_COLOR = {
  create: "np-badge-success",
  update: "np-badge-info",
  delete: "np-badge-error",
  send_email: "np-badge-neutral",
  login: "np-badge-neutral",
};

function AuditRow({ entry }) {
  const when = entry.created_at ? new Date(entry.created_at) : null;
  const timeStr = (when && !isNaN(when.getTime()))
    ? when.toLocaleString("es-CR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";
  const actor = entry.user_name || entry.user_email || "—";
  const initialsActor = actor.split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return (
    <div className="py-3 flex items-start gap-3" data-testid={`audit-row-${entry.id}`}>
      <div className="w-8 h-8 rounded-full bg-[#E6EFEA] text-primary-color flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
        {initialsActor}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm">
          <strong className="text-primary-c">{actor}</strong>{" "}
          <span className={`np-badge text-[10px] mx-1 ${ACTION_COLOR[entry.action] || "np-badge-neutral"}`}>{ACTION_LABEL[entry.action] || entry.action}</span>
          {ENTITY_LABEL[entry.entity_type] || entry.entity_type}
          {entry.entity_label ? <>: <strong className="text-primary-c">{entry.entity_label}</strong></> : null}
        </div>
        <div className="text-xs text-secondary-c mt-0.5">{timeStr}{entry.ip ? <> · {entry.ip}</> : null}</div>
      </div>
    </div>
  );
}


// ==================== Helpers de fecha / eventos ====================
function toISOLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function daysDiff(a, b) {
  const da = new Date(`${String(a).slice(0, 10)}T00:00:00`);
  const db = new Date(`${String(b).slice(0, 10)}T00:00:00`);
  return Math.round((db - da) / 86400000);
}
function plusOneDay(iso) {
  const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + 1);
  return toISOLocal(d);
}
function groupContiguous(rows) {
  const sorted = [...rows].sort((a, b) => (a.empleado_id || "").localeCompare(b.empleado_id || "") || String(a.fecha).localeCompare(String(b.fecha)));
  const out = [];
  for (const r of sorted) {
    const f = String(r.fecha).slice(0, 10);
    const g = out.find(x => x.empleado_id === r.empleado_id && plusOneDay(x.fecha_fin) === f);
    if (g) { g.fecha_fin = f; g.dias += 1; }
    else { out.push({ empleado_id: r.empleado_id, fecha_inicio: f, fecha_fin: f, dias: 1 }); }
  }
  return out;
}
// Expande un evento con rango (fecha..fechaFin) en un array de fechas ISO
function expandRange(fechaIni, fechaFin) {
  if (!fechaIni) return [];
  const out = [];
  const start = new Date(`${fechaIni}T00:00:00`);
  const end = fechaFin ? new Date(`${fechaFin}T00:00:00`) : start;
  const cur = new Date(start);
  while (cur <= end) {
    out.push(toISOLocal(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

const TIPO_META = {
  cumpleanios: { label: "Cumpleaños", Icon: Cake, dot: "#D96C4A", bg: "bg-[#FDF1EC]" },
  incapacidad: { label: "Incapacidad", Icon: Heart, dot: "#B84B4B", bg: "bg-[#FDEDED]" },
  vacaciones:  { label: "Vacaciones",  Icon: Palmtree, dot: "#2F7A5F", bg: "bg-[#E6EFEA]" },
  solicitud:   { label: "Solicitud",   Icon: ListChecks, dot: "#A67115", bg: "bg-[#FFF4E5]" },
  ascenso:     { label: "Fin prueba",  Icon: ArrowUpCircle, dot: "#6D4AA6", bg: "bg-[#F1ECF7]" },
  revision:    { label: "Revisión",    Icon: Briefcase, dot: "#274735", bg: "bg-[#E6EFEA]" },
  accion_documento:   { label: "Acción de personal", Icon: FileSignature, dot: "#5A4A7A", bg: "bg-[#F1ECF7]" },
  pago_suscripcion:   { label: "Pago suscripción",    Icon: CreditCard, dot: "#B84B4B", bg: "bg-[#FDEDED]" },
  custom_tarea:        { label: "Tarea",         Icon: ClipboardList, dot: "#274735", bg: "bg-[#E6EFEA]" },
  custom_reunion:      { label: "Reunión",       Icon: UsersIcon, dot: "#2E5F87", bg: "bg-[#E5EEF7]" },
  custom_pago:         { label: "Pago",          Icon: CreditCard, dot: "#A67115", bg: "bg-[#FFF4E5]" },
  custom_recordatorio: { label: "Recordatorio",  Icon: Bell, dot: "#6D4AA6", bg: "bg-[#F1ECF7]" },
  custom_otro:         { label: "Nota",          Icon: CalendarDays, dot: "#6B7A88", bg: "bg-[#F0F0EA]" },
};

// Nombre corto para el calendario: "Nombre A." (primer nombre + inicial del apellido)
function shortName(fullName = "") {
  const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[1][0]}.`;
}


// ==================== Calendario del mes ====================
function MonthCalendar({ eventos, onCreateEvent, onDeleteCustomEvent }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [dayModal, setDayModal] = useState(null); // ISO string del día seleccionado

  // Index: fecha ISO -> [eventos] (incluye días intermedios de rangos)
  const byDate = useMemo(() => {
    const idx = {};
    for (const ev of eventos) {
      const dates = ev.fechaFin ? expandRange(ev.fecha, ev.fechaFin) : [ev.fecha];
      for (const d of dates) {
        (idx[d] = idx[d] || []).push(ev);
      }
    }
    return idx;
  }, [eventos]);

  const firstOfMonth = new Date(cursor.y, cursor.m, 1);
  const startDay = (firstOfMonth.getDay() + 6) % 7; // Lunes=0
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.y, cursor.m, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const monthName = new Date(cursor.y, cursor.m, 1).toLocaleDateString("es-CR", { month: "long", year: "numeric" });
  const totalMes = Object.entries(byDate).filter(([iso]) => iso.startsWith(`${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}`)).length;

  const goPrev = () => setCursor(c => c.m === 0 ? { y: c.y - 1, m: 11 } : { ...c, m: c.m - 1 });
  const goNext = () => setCursor(c => c.m === 11 ? { y: c.y + 1, m: 0 } : { ...c, m: c.m + 1 });
  const goToday = () => setCursor({ y: today.getFullYear(), m: today.getMonth() });

  return (
    <div className="np-card fade-in-up" data-testid="dashboard-calendar">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-[#E6EFEA] text-primary-color flex items-center justify-center">
            <CalendarDays size={20} strokeWidth={1.8} />
          </div>
          <div>
            <h3 className="font-display text-xl font-semibold leading-tight capitalize">{monthName}</h3>
            <p className="text-xs text-secondary-c">{totalMes === 0 ? "Sin eventos en este mes" : `${totalMes} día${totalMes === 1 ? "" : "s"} con eventos`}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={goPrev} className="np-btn np-btn-ghost p-2" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <button onClick={goToday} className="np-btn np-btn-outline text-xs px-3 py-1.5" data-testid="cal-today">Hoy</button>
          <button onClick={goNext} className="np-btn np-btn-ghost p-2" data-testid="cal-next"><ChevronRight size={16} /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map(d => (
          <div key={d} className="text-center text-[11px] font-semibold uppercase tracking-wider text-secondary-c pb-1">{d}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={`empty-${i}`} className="min-h-[110px]" />;
          const iso = toISOLocal(d);
          const evs = byDate[iso] || [];
          const isToday = iso === toISOLocal(today);
          const isWeekend = [5, 6].includes((i) % 7);
          return (
            <button
              key={iso}
              onClick={() => setDayModal(iso)}
              className={`min-h-[110px] p-1.5 rounded-xl border text-left transition-all overflow-hidden ${isToday ? "border-primary-c bg-[#F0F5F1]" : "border-c bg-white"} hover:shadow-sm hover:-translate-y-0.5 cursor-pointer ${isWeekend ? "bg-[#FAFAF7]" : ""}`}
              data-testid={`cal-day-${iso}`}
            >
              <div className={`text-[10px] font-semibold ${isToday ? "text-primary-c" : "text-secondary-c"} leading-none mb-1`}>{d.getDate()}</div>
              <div className="space-y-0.5">
                {evs.slice(0, 3).map(ev => {
                  const meta = TIPO_META[ev.tipo] || {};
                  return (
                    <div
                      key={ev.id + "-" + iso}
                      title={`${meta.label || ev.tipo} · ${ev.empleadoNombre || ""}`}
                      className="text-[9.5px] leading-tight px-1 py-0.5 rounded truncate flex items-center gap-1"
                      style={{ background: `${ev.color}15`, color: ev.color }}
                    >
                      <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: ev.color }} />
                      <span className="truncate font-medium">{meta.label || ev.tipo}</span>
                      {ev.empleadoNombre && (
                        <span className="truncate opacity-75 hidden md:inline">· {shortName(ev.empleadoNombre)}</span>
                      )}
                    </div>
                  );
                })}
                {evs.length > 3 && (
                  <div className="text-[9px] text-secondary-c font-medium pl-1">+{evs.length - 3} más</div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Leyenda */}
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-secondary-c">
        {Object.entries(TIPO_META).map(([k, m]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: m.dot }} />
            {m.label}
          </span>
        ))}
      </div>

      {dayModal && <DayEventsModal iso={dayModal} eventos={byDate[dayModal] || []} onClose={() => setDayModal(null)} onCreateEvent={onCreateEvent} onDeleteCustomEvent={onDeleteCustomEvent} />}
    </div>
  );
}

function DayEventsModal({ iso, eventos, onClose, onCreateEvent, onDeleteCustomEvent }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ titulo: "", tipo: "tarea", hora: "", descripcion: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.titulo.trim()) { toast.error("Escribí un título"); return; }
    setSaving(true);
    try {
      await onCreateEvent({ ...form, fecha: iso });
      setCreating(false);
      setForm({ titulo: "", tipo: "tarea", hora: "", descripcion: "" });
    } catch (err) {
      toast.error("No se pudo crear el evento");
    } finally { setSaving(false); }
  };

  const TIPOS = [
    { key: "tarea", label: "Tarea", color: "#274735" },
    { key: "reunion", label: "Reunión", color: "#2E5F87" },
    { key: "pago", label: "Pago", color: "#A67115" },
    { key: "recordatorio", label: "Recordatorio", color: "#6D4AA6" },
    { key: "otro", label: "Otro", color: "#6B7A88" },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="np-card p-6 max-w-lg w-full max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="cal-day-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold">Eventos del día</div>
            <h3 className="font-display text-xl font-semibold capitalize">{fmtFecha(iso)}</h3>
          </div>
          <div className="flex items-center gap-1">
            {!creating && (
              <button onClick={() => setCreating(true)} className="np-btn np-btn-primary text-xs" data-testid="cal-day-add-event">
                <Plus size={14} /> Agregar
              </button>
            )}
            <button onClick={onClose} className="np-btn np-btn-ghost p-2" data-testid="cal-day-close"><X size={18} /></button>
          </div>
        </div>

        {creating && (
          <form onSubmit={submit} className="np-card p-4 mb-4 space-y-3 bg-[#FAFAF7]" data-testid="cal-day-create-form">
            <div>
              <label className="np-label">Título</label>
              <input value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} className="np-input" placeholder="Ej. Pagar seguro social, Reunión con contador…" data-testid="event-titulo" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="np-label">Tipo</label>
                <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} className="np-input" data-testid="event-tipo">
                  {TIPOS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="np-label">Hora (opcional)</label>
                <input type="time" value={form.hora} onChange={e => setForm(f => ({ ...f, hora: e.target.value }))} className="np-input" data-testid="event-hora" />
              </div>
            </div>
            <div>
              <label className="np-label">Descripción (opcional)</label>
              <textarea value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} className="np-input min-h-[70px]" placeholder="Detalles adicionales…" data-testid="event-descripcion" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setCreating(false)} className="np-btn np-btn-ghost" data-testid="event-cancel">Cancelar</button>
              <button type="submit" disabled={saving} className="np-btn np-btn-primary" data-testid="event-save">
                {saving ? "Guardando…" : "Guardar evento"}
              </button>
            </div>
          </form>
        )}

        {eventos.length === 0 && !creating ? (
          <div className="text-sm text-secondary-c">Sin eventos. Click en <strong>Agregar</strong> para crear una tarea, reunión o pago programado.</div>
        ) : eventos.length > 0 && (
          <div className="space-y-2">
            {eventos.map(ev => {
              const meta = TIPO_META[ev.tipo] || {};
              const Icon = meta.Icon || CalendarDays;
              const rangoTexto = ev.fechaFin && ev.fecha !== ev.fechaFin
                ? `Del ${fmtFecha(ev.fecha)} al ${fmtFecha(ev.fechaFin)}`
                : null;
              const inner = (
                <>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.bg || "bg-[#F0F0EA]"}`} style={{ color: ev.color }}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{ev.titulo}</div>
                    {rangoTexto && (
                      <div className="text-[11px] text-secondary-c mt-0.5 inline-flex items-center gap-1"><Clock size={10} />{rangoTexto}</div>
                    )}
                    {ev.sub && <div className="text-xs text-secondary-c truncate mt-0.5">{ev.sub}</div>}
                  </div>
                  <span className="np-badge text-[10px]" style={{ background: `${ev.color}18`, color: ev.color }}>{meta.label || ev.tipo}</span>
                </>
              );
              if (ev.isCustom) {
                return (
                  <div key={ev.id} className="flex items-start gap-3 p-3 rounded-xl border border-c bg-white" data-testid={`cal-day-event-${ev.id}`}>
                    {inner}
                    <button
                      onClick={(e) => { e.stopPropagation(); if (window.confirm("¿Eliminar este evento?")) onDeleteCustomEvent(ev.customId); }}
                      className="np-btn np-btn-ghost p-1.5 text-[#B84B4B] hover:bg-[#FDEDED]"
                      title="Eliminar"
                      data-testid={`cal-day-delete-${ev.customId}`}
                    ><X size={14} /></button>
                  </div>
                );
              }
              return (
                <Link key={ev.id} to={ev.url || "/dashboard"} onClick={onClose}
                  className="flex items-start gap-3 p-3 rounded-xl border border-c bg-white hover:bg-[#FAFAF7] transition-colors"
                  data-testid={`cal-day-event-${ev.id}`}>
                  {inner}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// ==================== Recordatorios (media pantalla, al lado de Solicitudes) ====================
function Recordatorios({ eventos }) {
  const [filter, setFilter] = useState("todos");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const hoyISO = toISOLocal(today);

  const proximos = useMemo(() => {
    return eventos
      // Excluimos: revision, cumpleanios y solicitud — todos tienen su propia sección en el dashboard
      .filter(ev => ev.tipo !== "revision" && ev.tipo !== "cumpleanios" && ev.tipo !== "solicitud")
      // Eventos futuros o "en curso" (con rango que aún no termina)
      .filter(ev => ev.fecha >= hoyISO || (ev.fechaFin && ev.fechaFin >= hoyISO))
      .filter(ev => filter === "todos" || ev.tipo === filter)
      .sort((a, b) => a.fecha.localeCompare(b.fecha))
      .slice(0, 30);
  }, [eventos, hoyISO, filter]);

  const filters = [
    { key: "todos", label: "Todos" },
    { key: "vacaciones", label: "Vacaciones" },
    { key: "incapacidad", label: "Incapacidades" },
    { key: "ascenso", label: "Fin período prueba" },
  ];

  return (
    <div className="np-card" data-testid="dashboard-recordatorios">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-[#F1ECF7] text-[#6D4AA6] flex items-center justify-center">
            <Bell size={20} strokeWidth={1.8} />
          </div>
          <div>
            <h3 className="font-display text-xl font-semibold leading-tight">Recordatorios</h3>
            <p className="text-xs text-secondary-c">Eventos en curso y próximos, ordenados por fecha</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1 flex-wrap mb-3">
        {filters.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === f.key ? "bg-primary-c text-white" : "bg-[#F0F0EA] text-primary-c hover:bg-[#E6EFEA]"}`}
            data-testid={`recordatorios-filter-${f.key}`}>
            {f.label}
          </button>
        ))}
      </div>

      {proximos.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 rounded-xl bg-[#FAFAF7] border border-dashed border-c">
          <Sparkles size={28} className="text-secondary-c opacity-60" />
          <div className="text-sm text-secondary-c">Sin recordatorios próximos.</div>
        </div>
      ) : (
        <div className="space-y-2 max-h-[520px] overflow-auto pr-1" data-testid="recordatorios-list">
          {proximos.map(ev => {
            const meta = TIPO_META[ev.tipo] || {};
            const Icon = meta.Icon || CalendarDays;
            // "En curso" cuando el evento tiene rango y hoy cae dentro
            const isEnCurso = ev.fechaFin && ev.fecha <= hoyISO && hoyISO <= ev.fechaFin;
            const dias = daysDiff(hoyISO, ev.fecha);
            const cuando = isEnCurso ? "En curso"
              : dias === 0 ? "Hoy"
              : dias === 1 ? "Mañana"
              : dias > 0 ? `En ${dias} d`
              : `Hace ${-dias} d`;
            const cuandoColor = isEnCurso ? "text-error font-semibold"
              : dias === 0 ? "text-error font-semibold"
              : dias <= 3 ? "text-warning font-semibold"
              : dias <= 7 ? "text-primary-c font-medium"
              : "text-secondary-c";
            // Rango o fecha única
            const rangoTexto = ev.fechaFin && ev.fecha !== ev.fechaFin
              ? `Del ${fmtFecha(ev.fecha)} al ${fmtFecha(ev.fechaFin)}`
              : fmtFecha(ev.fecha);
            return (
              <Link key={ev.id} to={ev.url || "/dashboard"}
                className="flex items-start gap-3 p-3 rounded-xl border border-c bg-white hover:bg-[#FAFAF7] transition-colors"
                data-testid={`recordatorio-${ev.id}`}>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.bg || "bg-[#F0F0EA]"}`} style={{ color: ev.color }}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{ev.titulo}</div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-[11px] text-secondary-c inline-flex items-center gap-1"><Clock size={10} />{rangoTexto}</span>
                    {ev.sub && <span className="text-[11px] text-secondary-c truncate">· {ev.sub}</span>}
                  </div>
                </div>
                <div className={`text-[11px] flex-shrink-0 ${cuandoColor}`}>{cuando}</div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
